import React, { useState, useEffect } from 'react';
import { fetchDocumentTypes } from '../services/documentTypes';
import { DocumentType } from '../utils/documentTypes';
import Icon from './icons';
import { useTranslation } from 'react-i18next';

interface DocumentUploadFormProps {
  imageFile: File;
  imagePreview: string;
  onSubmit: (documentType: string, notes: string) => void;
  onCancel: () => void;
  predefinedType?: string; // Add optional predefined document type
}

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  imageFile,
  imagePreview,
  onSubmit,
  onCancel,
  predefinedType
}) => {
  const { t } = useTranslation();
  // If predefinedType is provided, use it as the initial state
  const [documentType, setDocumentType] = useState(predefinedType || '');
  const [notes, setNotes] = useState('');
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Load document types
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const types = await fetchDocumentTypes();
        setDocumentTypes(types);
      } catch (error) {
        console.error('Error loading document types:', error);
      }
    };
    
    loadDocumentTypes();
  }, []);
  
  // If predefinedType changes, update the documentType state
  useEffect(() => {
    if (predefinedType) {
      setDocumentType(predefinedType);
    }
  }, [predefinedType]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // If AI_DETECT is selected, pass empty string to let backend decide
    const typeToSubmit = documentType === 'AI_DETECT' ? '' : documentType;
    onSubmit(typeToSubmit, notes);
  };

  // Get document type icon based on document type
  const getDocumentTypeIcon = (type: string, size = 24) => {
    // Map document types to icon names for the Icon component
    const iconMap: {[key: string]: string} = {
      'BLOOD_TEST': 'OutlineScience',
      'OPTICAL_EXAM': 'OutlineRemoveRedEye',
      'MEDICAL_RECORD': 'OutlineDescription',
      'PRESCRIPTION': 'OutlineMedicalServices',
      'IMAGING_REPORT': 'OutlineBiotech',
      'LAB_REPORT': 'OutlineScience',
      'DISCHARGE_SUMMARY': 'OutlineLocalHospital',
      'PATHOLOGY_REPORT': 'OutlineBiotech',
      'VACCINATION_RECORD': 'OutlineVaccines',
      'SURGICAL_REPORT': 'OutlineMedicalServices',
      'OTHER': 'OutlineImage'
    };
    
    // Get the icon name from the map or use a default
    const iconName = iconMap[type] || 'OutlineImage';
    
    // Return the Icon component with the appropriate icon name
    return <Icon iconName={iconName} size={size} color="currentColor" />;
  };

  // Filter document types based on predefinedType
  const getFilteredDocumentTypes = () => {
    if (predefinedType) {
      // If predefined type is provided, only show that type
      return documentTypes.filter(type => type.code === predefinedType);
    }
    return documentTypes;
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">{t('documentUpload.title')}</h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">{t('common.close')}</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            {/* Image preview first */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('documentUpload.imagePreview')}
              </label>
              <div className="border rounded-md overflow-hidden bg-gray-100 h-48 flex items-center justify-center">
                <img
                  src={imagePreview}
                  alt={t('documentUpload.documentPreview')}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {t('documentUpload.filename')}: {imageFile.name}
              </div>
            </div>
            
            {/* Document type selection - only show if no predefined type or show only the predefined type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {predefinedType ? t('documentUpload.documentType') : t('documentUpload.selectDocumentType')}
              </label>
              
              {/* Document type options in a grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {/* Show filtered document types */}
                {getFilteredDocumentTypes().map((type) => (
                  <button
                    key={type.code}
                    type="button"
                    onClick={() => setDocumentType(type.code)}
                    className={`flex flex-col items-center p-3 rounded-lg border ${
                      documentType === type.code 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    disabled={!!predefinedType} // Disable button if predefined type is provided
                  >
                    <div className="mb-2">
                      {getDocumentTypeIcon(type.code, 28)}
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm">
                        {t(`documentUpload.documentTypes.${type.code.toLowerCase()}`, {defaultValue: type.name_zh})}
                      </div>
                    </div>
                  </button>
                ))}
                
                {/* Only show Other and AI options if no predefined type */}
                {!predefinedType && (
                  <>
                    {/* Other option */}
                    <button
                      type="button"
                      onClick={() => setDocumentType('OTHER')}
                      className={`flex flex-col items-center p-3 rounded-lg border ${
                        documentType === 'OTHER' 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="mb-2">
                        {getDocumentTypeIcon('OTHER', 28)}
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-sm">{t('documentUpload.documentTypes.other')}</div>
                      </div>
                    </button>
                    
                    {/* AI Auto-detect option (last) */}
                    <button
                      type="button"
                      onClick={() => setDocumentType('')}
                      className={`flex flex-col items-center p-3 rounded-lg border ${
                        documentType === '' 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="mb-2">
                        <Icon iconName="OutlineAuto" size={28} color={documentType === '' ? '#4f46e5' : '#6b7280'} />
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-sm">{t('documentUpload.documentTypes.aiDetect')}</div>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {/* Notes field */}
            <div className="mb-4">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                {t('documentUpload.notes')}
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('documentUpload.notesPlaceholder')}
              />
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('documentUpload.uploading')}
                </>
              ) : (
                t('documentUpload.uploadDocument')
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadForm; 